import { computed, effect, inject, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { BooleanStorageKeys, FeatureConfigKeys, StorageKeys, StorageService, StorageType } from '../storage-service/storage.service';
import { ServiceUtils } from '../service-utils/service-utils';
import {
    BackendType,
    Branch,
    Branding,
    DviFeatureConfig,
    FeatureFlagTypes,
    INSPECTION_REVIEW_GATE_SETTING_KEY,
    Language,
    PermissionKey,
    Setting,
    TimeClockEventType,
    TranslationEntry,
} from '@raptor-fe/core';
import { PushNotificationService } from '../push-notification-service/push-notification.service';
import { NetworkStatusService } from '../network-status-service/network-status.service';
import { TokenService } from '../token-service/token.service';
import { TimeClockService } from '../time-clock-service/time-clock.service';
import { catchError, filter, firstValueFrom, map, of, pipe, switchMap, tap, timeout } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ToastService } from '../toast-service/toast.service';
import { URLService } from '../url-service/url.service';
import { ThemeService } from '../theme-service/theme.service';
import { AuthorizationService } from '../authorization-service/authorization.service';

// This interface defines the state of the application context store.
export interface ApplicationContextState {
    tenantId: string;
    branch: Branch;
    isStandaloneTenant: boolean;
    isSingleBranchUser: boolean;
    userId: string;
    username: string;
    displayName: string;
    isWebViewMode: boolean;
    isWebContentFluid: boolean;
    roles: string[];
    backendType: BackendType;
    featureConfigs: {
        dviFeatureConfig: DviFeatureConfig;
    };
    isLoggedIn: boolean;
    isWorkDayClockedIn: boolean;
    isLoadingWorkDayClockStatus: boolean;
    features: string[];
    /**
     * The tenant's settings for the currently-selected branch as a key/value map (SettingKey member
     * NAME -> string value). Branch-scoped (not tenant-wide): it is derived from the selected branch's
     * own `settings` array and re-derived whenever the selected branch changes. Derived flags such as
     * the inspection review gate ({@link ApplicationContextStore.reviewGateActiveForCurrentBranch}) are
     * read from this map. Defaults to an empty map and fails closed when no branch is selected or the
     * branch carries no settings.
     */
    settings: Record<string, string>;
    permissions: string[];
    permissionsLoaded: boolean;
    dictionary: Map<string, string> | null;
    selectedLanguage: Language;
    shouldIncludeOriginalLabelInTranslation: boolean; // TODO: Set to true to include the original label alongside the translation (e.g., "Translated/Original")
    branding: Branding | null;
    currentUrl: string;
    isReady: boolean;
}

export const WebViewNotificationEventTypes = {
    CUSTOMER_DETAILS: 'customer-details',
    CUSTOMER_SAVED: 'customer-saved',
    INSPECTION_TEMPLATE_SAVED: 'inspection-template-saved',
    USER_SAVED: 'user-saved',
    QUOTE_DETAILS: 'quote-details',
    WORKORDER_DETAILS: 'workorder-details',
};

/**
 * How long the boot-time supported-language lookup may take before the stored selection stands.
 *
 * This call gates isReady, and applicationContextReadyGuard waits on isReady without a timeout of
 * its own, so an unbounded request parks every app shell -- including the anonymous self-check-in
 * route -- for as long as the platform allows. Mirrors the branding fetch in code-login.store.ts.
 */
const LANGUAGE_LOOKUP_TIMEOUT_MS = 3000;

const initialState: ApplicationContextState = {
    tenantId: '',
    branch: { id: '', name: '' },
    isStandaloneTenant: false,
    isSingleBranchUser: false,
    userId: '',
    username: '',
    displayName: '',
    isWebViewMode: false,
    isWebContentFluid: false,
    roles: [],
    backendType: BackendType.NONE,
    featureConfigs: {
        dviFeatureConfig: {
            vehicleEditDisabledFields: [],
        },
    },
    isLoggedIn: false,
    isWorkDayClockedIn: false,
    isLoadingWorkDayClockStatus: false,
    features: [],
    settings: {},
    permissions: [],
    permissionsLoaded: false,
    dictionary: null,
    selectedLanguage: { id: '', name: '' },
    shouldIncludeOriginalLabelInTranslation: false,
    branding: null,
    currentUrl: '',
    isReady: false,
};

// TODO: Move apps from sync service to use this store gradually
// This store manages the application context, starting with properties like `isWebViewMode` and `roles`.
// It uses the `StorageService` to persist these properties in local storage.
// It initializes these properties from local storage and provides methods to update them.
export const ApplicationContextStore = signalStore(
    { providedIn: 'root' },
    withState<ApplicationContextState>(initialState),
    withComputed((store) => ({
        /**
         * The effective permission keys for the current user/branch as an O(1) lookup set. Admin
         * and system roles project to the full catalog server-side, so no privileged-role
         * special-casing is needed here -- a bypass user simply holds every key.
         */
        permissionSet: computed(() => new Set<string>(store.permissions())),
        /**
         * Whether the inspection review gate is active for the user's currently-selected branch. Derived
         * from the branch-scoped settings map: true only when the map carries
         * {@link INSPECTION_REVIEW_GATE_SETTING_KEY} with the string value 'true'. Any other value, a missing key, or
         * an empty map (the fail-closed state) yields false, so gate-dependent UI never opens on a stale or
         * cross-branch value.
         */
        reviewGateActiveForCurrentBranch: computed(() => store.settings()[INSPECTION_REVIEW_GATE_SETTING_KEY] === 'true'),
    })),
    withHooks(
        (
            store,
            storageService = inject(StorageService),
            tokenService = inject(TokenService),
            pushNotificationService = inject(PushNotificationService),
            networkStatusService = inject(NetworkStatusService),
            urlService = inject(URLService),
            themeService = inject(ThemeService),
            router = inject(Router),
            http = inject(HttpClient),
            serviceUtils = inject(ServiceUtils),
        ) => ({
            onInit: async () => {
                router.events
                    .pipe(
                        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                        map((e) => e.urlAfterRedirects),
                    )
                    .subscribe((url) => patchState(store, { currentUrl: url }));

                try {
                    const tenantId = await storageService.getString(StorageKeys.TENANT_ID);
                    const branch = await storageService.getObject<Branch>(StorageKeys.SELECTED_BRANCH);
                    const isStandaloneTenant = await storageService.getBoolean(BooleanStorageKeys.IS_STANDALONE_TENANT);
                    const isSingleBranchUser = await storageService.getBoolean(BooleanStorageKeys.IS_SINGLE_BRANCH_USER);
                    const userId = await storageService.getString(StorageKeys.USER_ID);
                    const username = await storageService.getString(StorageKeys.USERNAME);
                    const displayName = await storageService.getString(StorageKeys.DISPLAY_NAME);
                    const storedIsWebViewMode = await storageService.getBoolean(BooleanStorageKeys.IS_WEB_VIEW_MODE);
                    const isWebContentFluid = await storageService.getBoolean(BooleanStorageKeys.IS_WEB_CONTENT_FLUID, false);
                    const roles = await storageService.getObject<string[]>(StorageKeys.ROLES);
                    const backendType = await storageService.getObject<BackendType>(StorageKeys.BACKEND_TYPE);
                    const dviFeatureConfig = await storageService.getObject<DviFeatureConfig>(FeatureConfigKeys.DVI_FEATURE_CONFIG);
                    const features = await storageService.getObject<string[]>(StorageKeys.FEATURES);
                    const settings = await storageService.getObject<Record<string, string>>(StorageKeys.SETTINGS);
                    const permissions = await storageService.getObject<string[]>(StorageKeys.PERMISSIONS);
                    const dictionaryEntries = await storageService.getObject<TranslationEntry[]>(StorageKeys.DICTIONARY);
                    const selectedLanguage = await storageService.getObject<Language>(StorageKeys.LANGUAGE_SELECTED_KEY);
                    const branding = await storageService.getObject<Branding>(StorageKeys.BRANDING);
                    const hasStoredSelection = !!selectedLanguage?.id;
                    const token = tokenService.getToken();
                    const isLoggedIn = token !== null && token !== '';

                    /**
                     * External bootstrap-time callers (LoginComponent, CodeLoginComponent,
                     * self-check-in session) can invoke setIsWebViewMode(true) synchronously
                     * from their queryParams subscribe while this onInit is still awaiting
                     * storage reads. The value read from storage is stale by then, and no code
                     * path re-reads the URL flag afterwards, so a value that has diverged from
                     * the initial default wins. The storage type follows the same value: the
                     * services must keep writing a link session's token to sessionStorage, or
                     * it outlives the tab and the next link open starts from an expired session.
                     */
                    const isWebViewMode = store.isWebViewMode() !== initialState.isWebViewMode ? store.isWebViewMode() : (storedIsWebViewMode ?? false);
                    const storageType = isWebViewMode ? StorageType.SESSION : StorageType.LOCAL;

                    if (isLoggedIn && !isWebViewMode) {
                        pushNotificationService.initialize();
                        networkStatusService.initialize();
                    }

                    storageService.setStorageType(storageType);
                    tokenService.setStorageType(storageType);
                    urlService.setStorageType(storageType);

                    patchState(store, {
                        tenantId: tenantId.value ?? '',
                        branch: branch ?? { id: '', name: '' },
                        isStandaloneTenant: isStandaloneTenant ?? false,
                        isSingleBranchUser: isSingleBranchUser,
                        userId: userId.value ?? '',
                        username: username.value ?? '',
                        displayName: displayName.value ?? '',
                        isWebViewMode,
                        isWebContentFluid: isWebContentFluid ?? true,
                        roles: roles ?? [],
                        backendType: backendType ?? store.backendType(),
                        featureConfigs: {
                            dviFeatureConfig: dviFeatureConfig ?? store.featureConfigs.dviFeatureConfig(),
                        },
                        isLoggedIn: isLoggedIn ?? false,
                        features: features ?? [],
                        settings: settings ?? {},
                        permissions: permissions ?? [],
                        permissionsLoaded: permissions != null,
                        dictionary: dictionaryEntries ? convertDictionaryArrayToMap(dictionaryEntries) : null,
                        selectedLanguage: selectedLanguage?.id ? selectedLanguage : { id: '', name: '' },
                        branding: branding ?? null,
                    });

                    if (branding) {
                        themeService.applyBranding(branding);
                    }

                    // Awaited so nothing reads the language before it resolves. Runs even with a stored
                    // selection: deployments can drop languages, and single-language UI offers no way back.
                    await firstValueFrom(http.get<Language[]>(serviceUtils.getBaseUrl('language')).pipe(timeout(LANGUAGE_LOOKUP_TIMEOUT_MS)))
                        .then(async (languages) => {
                            const isStoredStillSupported = languages?.some((language) => language.id === selectedLanguage?.id) ?? false;
                            if (hasStoredSelection && isStoredStillSupported) {
                                return;
                            }

                            const backendDefault = languages?.find((language) => language.isDefault) ?? languages?.[0];
                            if (!backendDefault?.id) {
                                return;
                            }
                            await storageService.setObject(StorageKeys.LANGUAGE_SELECTED_KEY, backendDefault);

                            // The cached dictionary belongs to the language just dropped, so clearing it shows raw
                            // labels until the next fetch (login, or the boot refresh where an app provides one).
                            await storageService.setObject(StorageKeys.DICTIONARY, []);
                            patchState(store, { selectedLanguage: backendDefault, dictionary: null });
                        })
                        .catch((error) => {
                            console.error('Failed to fetch default language from backend; using locale-driven fallback.', error);
                        });
                } finally {
                    patchState(store, { isReady: true });
                }
            },
        }),
    ),
    withComputed((store) => ({
        hasAnyMobileFeaturesEnabled: computed(() =>
            store
                .features()
                .some((feature) =>
                    [
                        FeatureFlagTypes.DigitalVehicleInspection,
                        FeatureFlagTypes.InventoryCount,
                        FeatureFlagTypes.TimeClock,
                        FeatureFlagTypes.TechWorkView,
                    ].includes(feature as FeatureFlagTypes),
                ),
        ),
        /**
         * Whether the current session is an external customer view. This is an identity/shell
         * determination rather than a feature gate, so it remains role-based: the session is a
         * customer view when the only role held is 'customer'. Permission-based detection was
         * deliberately not adopted here to avoid an app-shell flash before permissions resolve and
         * to keep shared-link customer report viewers working.
         */
        isCustomerView: computed(() => {
            const roles = store.roles();
            return roles.length === 1 && roles[0].toLowerCase() === 'customer';
        }),
    })),
    withMethods((store, storageService = inject(StorageService), authorizationService = inject(AuthorizationService)) => ({
        /**
         * Whether the current user holds the given permission key. Admin/system roles project to the
         * full catalog server-side, so privileged users satisfy any key without special-casing.
         *
         * @param permission The permission key to check.
         * @returns True when the effective permission set contains the key.
         */
        hasPermission: (permission: PermissionKey | string): boolean => store.permissionSet().has(permission),
        /**
         * Whether the current user holds at least one of the given permission keys.
         *
         * @param permissions The candidate permission keys.
         * @returns True when the effective permission set contains any of the keys.
         */
        hasAnyPermission: (permissions: Array<PermissionKey | string>): boolean => {
            const set = store.permissionSet();
            return permissions.some((permission) => set.has(permission));
        },
        /**
         * Replaces the effective permission set and persists it, marking permissions as loaded.
         *
         * @param permissions The effective permission keys for the current user/branch.
         */
        setPermissions: async (permissions: string[]) => {
            patchState(store, { permissions, permissionsLoaded: true });
            await storageService.setObject<string[]>(StorageKeys.PERMISSIONS, permissions);
        },
        /**
         * Loads the current user's effective permission keys for their current branch from
         * GET /api/v1/me/permissions, patches them into state, and persists them for synchronous
         * hydration on the next session. On failure the set is cleared and marked loaded so gated UI
         * fails closed (nothing granted) rather than admitting access with a stale or cross-branch set,
         * while still resolving so permission-gated UI does not hang in an indeterminate state.
         */
        loadMyPermissions: rxMethod<void>(
            pipe(
                switchMap(() =>
                    authorizationService.getMyPermissions(store.tenantId()).pipe(
                        tapResponse({
                            next: async (permissions: string[]) => {
                                const effectivePermissions = permissions ?? [];
                                patchState(store, { permissions: effectivePermissions, permissionsLoaded: true });
                                await storageService.setObject<string[]>(StorageKeys.PERMISSIONS, effectivePermissions);
                            },
                            error: () => patchState(store, { permissions: [], permissionsLoaded: true }),
                        }),
                        catchError(() => of(void 0)),
                    ),
                ),
            ),
        ),
    })),
    withMethods(
        (
            store,
            storageService = inject(StorageService),
            timeClockService = inject(TimeClockService),
            toastService = inject(ToastService),
            tokenService = inject(TokenService),
            urlService = inject(URLService),
            themeService = inject(ThemeService),
            networkStatusService = inject(NetworkStatusService),
        ) => ({
            reset: async () => {
                patchState(store, initialState);
                themeService.clearBranding();
                networkStatusService.stopListening();
            },
            setBranding: async (branding: Branding | null) => {
                patchState(store, { branding });
                if (branding) {
                    await storageService.setObject(StorageKeys.BRANDING, branding);
                    themeService.applyBranding(branding);
                } else {
                    await storageService.removeItem(StorageKeys.BRANDING);
                    themeService.clearBranding();
                }
            },
            setTenantId: async (tenantId: string) => {
                patchState(store, { tenantId });
                await storageService.setString(StorageKeys.TENANT_ID, tenantId);
            },
            setBranch: async (branch: Branch) => {
                const settings = settingsFromBranch(branch);
                patchState(store, { branch, settings, permissions: [], permissionsLoaded: false });
                await storageService.setObject(StorageKeys.SELECTED_BRANCH, branch);
                await storageService.setObject<Record<string, string>>(StorageKeys.SETTINGS, settings);
                store.loadMyPermissions();
            },
            /**
             * Patches a single entry into the branch-scoped settings map and persists the whole map, so
             * derived gate-dependent flags (e.g. {@link ApplicationContextStore.reviewGateActiveForCurrentBranch})
             * recompute immediately after an in-app settings save without waiting for a branch
             * re-selection. Callers are responsible for scoping this to the currently-selected branch; the
             * map simply holds whatever key/value the client is authorised to see.
             *
             * @param key The SettingKey member name.
             * @param value The setting value serialised as a string.
             */
            setSetting: async (key: string, value: string) => {
                const settings = { ...store.settings(), [key]: value };
                patchState(store, { settings });
                await storageService.setObject<Record<string, string>>(StorageKeys.SETTINGS, settings);
            },

            /**
             * Replaces the whole branch-scoped settings map from a branch's authoritative `settings`
             * array and persists it, so derived gate-dependent flags (e.g.
             * {@link ApplicationContextStore.reviewGateActiveForCurrentBranch}) recompute after a
             * tenant-scope save the current branch inherits -- without re-selecting the branch or
             * churning permissions the way {@link setBranch} would. The branch is the authority: its
             * `settings` array already resolves tenant inheritance server-side.
             *
             * @param branch The current branch whose resolved settings should replace the map.
             */
            setSettingsFromBranch: async (branch: Branch) => {
                const settings = settingsFromBranch(branch);
                patchState(store, { settings });
                await storageService.setObject<Record<string, string>>(StorageKeys.SETTINGS, settings);
            },

            setIsStandaloneTenant: async (isStandalone: boolean) => {
                patchState(store, { isStandaloneTenant: isStandalone });
                await storageService.setBoolean(BooleanStorageKeys.IS_STANDALONE_TENANT, isStandalone);
            },
            setSingleBranchUser: async (branch: Branch) => {
                const settings = settingsFromBranch(branch);
                patchState(store, { isSingleBranchUser: true, branch: branch, settings, permissions: [], permissionsLoaded: false });
                await storageService.setObject(StorageKeys.SELECTED_BRANCH, branch);
                await storageService.setObject<Record<string, string>>(StorageKeys.SETTINGS, settings);
                await storageService.setBoolean(BooleanStorageKeys.IS_SINGLE_BRANCH_USER, true);
                store.loadMyPermissions();
            },
            setUserId: async (userId: string) => {
                patchState(store, { userId });
                await storageService.setString(StorageKeys.USER_ID, userId);
            },
            setUsername: async (username: string) => {
                patchState(store, { username });
                await storageService.setString(StorageKeys.USERNAME, username);
            },
            setDisplayName: async (displayName: string) => {
                patchState(store, { displayName });
                await storageService.setString(StorageKeys.DISPLAY_NAME, displayName);
            },
            setIsWebViewMode: async (isWebViewMode: boolean) => {
                patchState(store, {
                    isWebViewMode,
                });
                // Update the storage type based on the web view mode so that the data is stored in the appropriate storage (sessionStorage for web view mode, localStorage otherwise)
                storageService.setStorageType(isWebViewMode ? StorageType.SESSION : StorageType.LOCAL);
                tokenService.setStorageType(isWebViewMode ? StorageType.SESSION : StorageType.LOCAL);
                urlService.setStorageType(isWebViewMode ? StorageType.SESSION : StorageType.LOCAL);

                // set the web view mode in storage so that it can be accessed by other tabs/windows and to persist the value across sessions for web view mode
                await storageService.setBoolean(BooleanStorageKeys.IS_WEB_VIEW_MODE, isWebViewMode);
            },
            setIsWebContentFluid: async (isWebContentFluid: boolean) => {
                patchState(store, {
                    isWebContentFluid,
                });
                await storageService.setBoolean(BooleanStorageKeys.IS_WEB_CONTENT_FLUID, isWebContentFluid);
            },
            // Retrieves the current web view mode state
            setRoles: async (roles: string[] | string) => {
                const newRoles = Array.isArray(roles) ? roles : [roles];
                patchState(store, {
                    roles: newRoles,
                });
                await storageService.setObject<string[]>(StorageKeys.ROLES, newRoles);
            },
            // Retrieves the current web view mode state
            setFeatures: async (features: string[] | string) => {
                const newFeatures = Array.isArray(features) ? features : [features];
                patchState(store, {
                    features: newFeatures,
                });
                await storageService.setObject<string[]>(StorageKeys.FEATURES, newFeatures);
            },
            // Notifies the webview control about an event
            notifyWebviewControl: (eventName: string, data?: unknown) => {
                if (store.isWebViewMode()) {
                    window.chrome?.webview?.postMessage?.(JSON.stringify({ type: eventName, data }));
                }
            },
            setBackendType: async (backendType: BackendType) => {
                await storageService.setObject<BackendType>(StorageKeys.BACKEND_TYPE, backendType);
                patchState(store, {
                    backendType,
                });
            },
            setDviFeatureConfig: async (updater: (current: DviFeatureConfig) => DviFeatureConfig) => {
                const dviFeatureConfig = updater(store.featureConfigs.dviFeatureConfig());
                await storageService.setObject<DviFeatureConfig>(FeatureConfigKeys.DVI_FEATURE_CONFIG, dviFeatureConfig);
                patchState(store, {
                    featureConfigs: {
                        dviFeatureConfig,
                    },
                });
            },
            setIsLoggedIn: (isLoggedIn: boolean) => {
                patchState(store, { isLoggedIn });

                if (isLoggedIn && !store.isWebViewMode()) {
                    networkStatusService.initialize();
                } else {
                    networkStatusService.stopListening();
                }
            },
            setCurrentLanguage: async (language: Language) => {
                patchState(store, { selectedLanguage: language });
                await storageService.setObject(StorageKeys.LANGUAGE_SELECTED_KEY, language);
            },
            setDictionary: async (dictionary: TranslationEntry[]) => {
                await storageService.setObject(StorageKeys.DICTIONARY, dictionary);
                patchState(store, { dictionary: convertDictionaryArrayToMap(dictionary) });
            },
            // Toggles the work day clock-in status
            toggleDayClockedIn: rxMethod<void>(
                pipe(
                    tap(() => patchState(store, { isLoadingWorkDayClockStatus: true })),
                    switchMap(() => {
                        const observable = store.isWorkDayClockedIn()
                            ? timeClockService.clockOut(store.tenantId(), store.branch().id as string, TimeClockEventType.WorkDay)
                            : timeClockService.clockIn(store.tenantId(), store.branch().id as string, TimeClockEventType.WorkDay);
                        return observable.pipe(
                            tapResponse({
                                next: async () => {
                                    patchState(store, { isWorkDayClockedIn: !store.isWorkDayClockedIn() });
                                    await storageService.setBoolean(BooleanStorageKeys.IS_WORKDAY_CLOCKED_IN, store.isWorkDayClockedIn());
                                    await toastService.showSuccess(
                                        `Successfully clocked ${store.isWorkDayClockedIn() ? 'in' : 'out'} for the work day.`,
                                    );
                                },
                                error: async (errorResult: { error: { message: string } }) => {
                                    await toastService.showError(
                                        `An error occurred while clocking in/out: ${errorResult.error.message}. Please try again.`,
                                    );
                                },
                                finalize: () => patchState(store, { isLoadingWorkDayClockStatus: false }),
                            }),
                        );
                    }),
                ),
            ),
            // get time clock status for Work Day. Gated on DailyClockIn because this preload
            // is only meaningful when the marlin-mobile Clock In / Clock Out button is
            // actually shown -- the parent-map cascade guarantees DailyClockIn on implies
            // TimeClock on, so the underlying endpoint remains reachable.
            fetchWorkDayClockStatus: rxMethod<void>(
                pipe(
                    tap(() => patchState(store, { isLoadingWorkDayClockStatus: true })),
                    switchMap(() => {
                        if (store.features().includes(FeatureFlagTypes.DailyClockIn) === false) {
                            patchState(store, { isLoadingWorkDayClockStatus: false });
                            return new Promise<void>((resolve) => resolve());
                        }

                        return timeClockService
                            .isClockedIn(store.tenantId(), store.branch().id as string, TimeClockEventType.WorkDay, null, false)
                            .pipe(
                                tapResponse({
                                    next: async (isClockedIn) => {
                                        patchState(store, { isWorkDayClockedIn: isClockedIn });
                                        await storageService.setBoolean(BooleanStorageKeys.IS_WORKDAY_CLOCKED_IN, isClockedIn);
                                    },
                                    error: async (errorResult: { error: { message: string } }) => {
                                        await toastService.showError(
                                            `An error occurred while fetching work day clock status: ${errorResult.error.message}. Please try again.`,
                                        );
                                    },
                                    finalize: () => patchState(store, { isLoadingWorkDayClockStatus: false }),
                                }),
                            );
                    }),
                ),
            ),
        }),
    ),
    withHooks((store, tokenService = inject(TokenService), injector = inject(Injector)) => ({
        onInit: () => {
            const token = tokenService.getToken();
            if (!token) {
                return;
            }
            if (store.tenantId()) {
                store.loadMyPermissions();
                return;
            }
            const ref = effect(
                () => {
                    if (store.tenantId()) {
                        store.loadMyPermissions();
                        ref.destroy();
                    }
                },
                { injector },
            );
        },
    })),
);

// Extend the Window interface to include the chrome object with postmessage method
declare global {
    interface Window {
        chrome?: {
            webview?: {
                postMessage: (message: string) => void;
            };
        };
    }
}

/**
 * Reduces a branch's own `settings` array to a key/value map (SettingKey member NAME -> string value).
 * The branch is the authority for its settings, so callers derive the branch-scoped settings map from
 * this rather than from a separate endpoint. Fails closed to an empty map when the branch carries no
 * settings (e.g. the login inline `{ id, name: '' }` branch), so derived gate-dependent UI never opens
 * on a stale or cross-branch value until the welcome-screen's getAll repopulates the branch.
 *
 * @param branch The branch whose settings should be flattened.
 * @returns A key/value map of the branch's settings.
 */
function settingsFromBranch(branch: Branch): Record<string, string> {
    return (branch.settings ?? []).reduce<Record<string, string>>((map, setting: Setting) => {
        map[setting.key] = setting.value;
        return map;
    }, {});
}

function convertDictionaryArrayToMap(dictionary: TranslationEntry[] | null): Map<string, string> {
    return (
        dictionary?.reduce(
            (map, entry) => map.set(entry?.composedKey ?? `${entry.feature}|${entry.component}|${entry.label}`, entry.value),
            new Map<string, string>(),
        ) ?? new Map<string, string>()
    );
}
